import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Clock, PlayCircle, Play } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal, Kicker } from "@/components/motion";
import { api, loc, formatDate } from "@/lib/api";

const CATEGORIES = ["all", "fiscalite", "permis", "investissement", "architecture", "marche", "construction"];

export const ArticleCard = ({ article, testid }) => {
  const { t, i18n } = useTranslation();
  return (
    <Link
      to={`/novatv/${article.slug}`}
      data-testid={testid || `article-card-${article.slug}`}
      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-[#111827]/8 bg-white transition-shadow duration-500 hover:shadow-[0_20px_60px_rgba(17,24,39,0.12)]"
    >
      <div className="relative aspect-video overflow-hidden">
        <img src={article.cover} alt={loc(article.title, i18n.language)} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#111827] backdrop-blur-sm">
          {t(`tv.cats.${article.category}`)}
        </span>
        {article.type === "video" && (
          <span className="absolute inset-0 flex items-center justify-center" data-testid={`video-play-overlay-${article.slug}`}>
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/90 text-[#111827] shadow-lg backdrop-blur-sm transition-transform duration-300 group-hover:scale-110">
              <Play className="ml-0.5 h-5 w-5 fill-current" />
            </span>
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-6">
        <p className="text-xs text-slate-400">{formatDate(article.published_at, i18n.language)}</p>
        <h3 className="mt-2 font-display text-xl leading-snug text-[#111827]">{loc(article.title, i18n.language)}</h3>
        <div className="mt-auto flex items-center justify-between pt-5">
          <span className="flex items-center gap-1.5 text-xs text-slate-500">
            <Clock className="h-3.5 w-3.5" />
            {article.read_time} {t("tv.readTime")}
          </span>
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#2E8BFF]">
            {t("tv.read")}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </span>
        </div>
      </div>
    </Link>
  );
};

export default function NovaTV() {
  const { t, i18n } = useTranslation();
  const [category, setCategory] = useState("all");
  const [tab, setTab] = useState("all");
  const [articles, setArticles] = useState([]);
  const [hasVideos, setHasVideos] = useState(false);

  useEffect(() => {
    api.get("/novatv", { params: { type: "video" } })
      .then((r) => setHasVideos(r.data.length > 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const params = {};
    if (category !== "all") params.category = category;
    if (tab !== "all") params.type = tab;
    api.get("/novatv", { params })
      .then((r) => setArticles(r.data))
      .catch(() => {});
  }, [category, tab]);

  const videos = articles.filter((a) => a.type === "video");
  const mainList = tab === "all" ? articles.filter((a) => a.type !== "video") : articles;
  const [featured, ...rest] = mainList;

  return (
    <div className="bg-[#F8FAFC]" data-testid="novatv-page">
      <Seo page="novatv" />

      {/* Branded header */}
      <section className="grain relative overflow-hidden bg-[#111827] pb-20 pt-40">
        <motion.div
          aria-hidden="true"
          className="absolute -right-32 top-0 h-[420px] w-[420px] rounded-full bg-[#2E8BFF]/15 blur-3xl"
          animate={{ scale: [1, 1.15, 1] }}
          transition={{ repeat: Infinity, duration: 9, ease: "easeInOut" }}
        />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <p className="flex items-center gap-2.5 text-xs font-semibold uppercase tracking-[0.32em] text-[#4DB8FF]">
              <PlayCircle className="h-4 w-4" />
              {t("tv.kicker")}
            </p>
            <h1 className="mt-5 max-w-3xl font-display text-4xl text-white sm:text-5xl lg:text-6xl">{t("tv.title")}</h1>
            <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-white/70 md:text-lg">{t("tv.subtitle")}</p>
          </Reveal>
        </div>
      </section>

      {/* Type tabs (hidden while there are no videos) + category chips */}
      <section className="mx-auto max-w-7xl px-5 pt-12 lg:px-8" data-testid="tv-filter-bar">
        {hasVideos && (
          <div className="mb-5 inline-flex rounded-full border border-[#111827]/12 bg-white p-1" data-testid="tv-type-tabs">
          {["all", "video", "article"].map((tb) => (
            <button
              key={tb}
              data-testid={`tv-tab-${tb}`}
              onClick={() => setTab(tb)}
              className={`rounded-xl px-5 py-2 text-sm font-semibold transition-colors duration-300 ${
                tab === tb ? "bg-[#111827] text-white" : "text-[#111827]/60 hover:text-[#111827]"
              }`}
            >
              {t(`tv.tabs.${tb}`)}
            </button>
          ))}
          </div>
        )}
        <div className="flex flex-wrap gap-2.5">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              data-testid={`tv-filter-${cat}`}
              onClick={() => setCategory(cat)}
              className={`rounded-xl border px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${
                category === cat
                  ? "border-[#2E8BFF] bg-[#2E8BFF] text-white"
                  : "border-[#111827]/15 bg-white text-[#111827]/70 hover:border-[#111827]/40 hover:text-[#111827]"
              }`}
            >
              {t(`tv.cats.${cat}`)}
            </button>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-14 pb-28 lg:px-8">
        {articles.length === 0 ? (
          <p className="py-16 text-center text-slate-500" data-testid="tv-empty">{t("tv.empty")}</p>
        ) : (
          <>
            {/* Video rail (video-first space) */}
            {tab === "all" && videos.length > 0 && (
              <div className="mb-14" data-testid="tv-video-rail">
                <h2 className="mb-6 flex items-center gap-2.5 font-display text-2xl tracking-tight text-[#111827] md:text-3xl">
                  <PlayCircle className="h-7 w-7 text-[#2E8BFF]" />
                  {t("tv.videosTitle")}
                </h2>
                <div className="flex snap-x gap-5 overflow-x-auto pb-3">
                  {videos.map((v) => (
                    <div key={v.slug} className="w-[300px] shrink-0 snap-start sm:w-[340px]">
                      <ArticleCard article={v} testid={`video-card-${v.slug}`} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Featured latest */}
            {featured && (
              <AnimatePresence mode="wait">
                <motion.div
                  key={featured.slug}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <Link
                    to={`/novatv/${featured.slug}`}
                    data-testid="tv-featured-card"
                    className="group grid overflow-hidden rounded-2xl border border-[#111827]/8 bg-white transition-shadow duration-500 hover:shadow-[0_24px_70px_rgba(17,24,39,0.14)] lg:grid-cols-[1.3fr_1fr]"
                  >
                    <div className="relative aspect-video overflow-hidden lg:self-center">
                      <img src={featured.cover} alt={loc(featured.title, i18n.language)} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      {featured.type === "video" && (
                        <span className="absolute inset-0 flex items-center justify-center" data-testid="featured-video-play-overlay">
                          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/90 text-[#111827] shadow-lg backdrop-blur-sm transition-transform duration-300 group-hover:scale-110">
                            <Play className="ml-1 h-6 w-6 fill-current" />
                          </span>
                        </span>
                      )}
                    </div>
                    <div className="flex flex-col justify-center p-9 lg:p-12">
                      <span className="mb-4 inline-flex w-fit rounded-full bg-[#2E8BFF] px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-white">
                        {t("tv.featured")}
                      </span>
                      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#2E8BFF]">{t(`tv.cats.${featured.category}`)}</p>
                      <h2 className="mt-3 font-display text-2xl leading-snug text-[#111827] md:text-3xl">{loc(featured.title, i18n.language)}</h2>
                      <p className="mt-4 text-sm font-light leading-relaxed text-slate-600 md:text-base">{loc(featured.excerpt, i18n.language)}</p>
                      <div className="mt-6 flex items-center gap-4 text-xs text-slate-400">
                        <span>{formatDate(featured.published_at, i18n.language)}</span>
                        <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{featured.read_time} {t("tv.readTime")}</span>
                      </div>
                      <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#2E8BFF]">
                        {t("tv.read")}
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              </AnimatePresence>
            )}

            {/* Grid */}
            <motion.div layout className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="tv-grid">
              <AnimatePresence mode="popLayout">
                {rest.map((a) => (
                  <motion.div
                    key={a.slug}
                    layout
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                    className="h-full"
                  >
                    <ArticleCard article={a} />
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          </>
        )}
      </section>
    </div>
  );
}
